// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
import { absTime, depthKm, esc, joules, mag as fmtMag, num, relTime, tnt } from '../format';
import {
  depthCategory, energyCompare, energyJoules, feltVerdict, magClass, magColor,
} from '../model';
import { plateOf } from '../state';
import type { Quake } from '../types';
import { closeDrawer, gterm, infoI, openDrawer } from './overlays';

function alertChip(alert: string): string {
  if (!alert) return '';
  return `<span class="chip alert-${esc(alert)}">PAGER ${esc(alert)}</span>`;
}

/** Open the detail drawer for a single earthquake. Sets a shareable hash. */
export function openQuakeDrawer(q: Quake, onClose?: () => void): void {
  const j = energyJoules(q.mag);
  const plate = plateOf(q);
  const blast = q.type !== 'earthquake';
  const chips = [
    `<span class="chip">${esc(magClass(q.mag))}</span>`,
    q.tsunami ? '<span class="chip tsunami">Tsunami region</span>' : '',
    alertChip(q.alert),
    blast ? `<span class="chip blast">${esc(q.type)}</span>` : '',
  ].filter(Boolean).join(' ');

  const body = `
    <div class="between">
      <span class="mag-pill" style="background:${magColor(q.mag)};font-size:1.4rem;min-width:64px">${fmtMag(q.mag)}</span>
      <div style="text-align:right">
        <div class="muted" style="font-size:0.75rem">${esc(q.magType.toUpperCase() || 'MAG')} ${infoI('moment_magnitude')}</div>
        <div class="mono">${absTime(q.time)}</div>
      </div>
    </div>
    <div style="font-size:1.05rem;font-weight:600">${esc(q.place)}</div>
    <div>${chips}</div>
    <p class="callout">${esc(feltVerdict(q.mag))}</p>
    <dl class="kv">
      <dt>Depth ${infoI('depth')}</dt><dd>${depthKm(q.depth)} · ${esc(depthCategory(q.depth))}</dd>
      <dt>Energy ${infoI('energy')}</dt><dd>${joules(j)}</dd>
      <dt>TNT-equivalent</dt><dd>${tnt(j)}</dd>
      <dt>Comparison</dt><dd style="text-align:right;font-family:inherit">${esc(energyCompare(q.mag))}</dd>
      <dt>Felt reports ${infoI('dyfi')}</dt><dd>${q.felt ? num(q.felt) : '—'}</dd>
      <dt>Shaking (MMI) ${infoI('mmi')}</dt><dd>${q.mmi ? q.mmi.toFixed(1) : '—'}</dd>
      <dt>Significance ${infoI('significance')}</dt><dd>${num(q.sig)}</dd>
      <dt>Tectonic plate ${infoI('tectonic_plate')}</dt><dd style="text-align:right;font-family:inherit">${plate ? esc(plate) : '—'}</dd>
      <dt>Coordinates</dt><dd>${q.lat.toFixed(3)}, ${q.lon.toFixed(3)}</dd>
      <dt>Origin time</dt><dd>${relTime(q.time)}</dd>
    </dl>
    ${q.url ? `<a href="${esc(q.url)}" target="_blank" rel="noopener">Full details on USGS ↗</a>` : ''}
  `;
  openDrawer('Earthquake detail', body, () => {
    if (location.hash.startsWith('#event=')) history.replaceState(null, '', location.pathname + location.search);
    onClose?.();
  });
  history.replaceState(null, '', `#event=${encodeURIComponent(q.id)}`);
}

/** Open a drawer summarising a region / tectonic plate. */
export function openRegionDrawer(name: string, list: Quake[]): void {
  const sorted = [...list].sort((a, b) => b.mag - a.mag);
  const totalE = list.reduce((s, q) => s + energyJoules(q.mag), 0);
  const felt = list.reduce((s, q) => s + q.felt, 0);
  const shallow = list.filter((q) => depthCategory(q.depth) === 'Shallow').length;
  const rows = sorted.slice(0, 12).map((q) => `
    <div class="rank-row clickable" data-event="${esc(q.id)}" tabindex="0" role="button" data-tip="${esc(q.place)}">
      <span class="mag-pill" style="background:${magColor(q.mag)}">${fmtMag(q.mag)}</span>
      <div class="rank-label" style="grid-column:span 1">${esc(q.place)}</div>
      <div class="rank-value">${relTime(q.time)}</div>
    </div>`).join('');
  const body = `
    <dl class="kv">
      <dt>Events in window</dt><dd>${num(list.length)}</dd>
      <dt>Largest</dt><dd>M ${fmtMag(sorted[0]?.mag ?? NaN)}</dd>
      <dt>Total energy ${infoI('energy')}</dt><dd>${joules(totalE)}</dd>
      <dt>Shallow share</dt><dd>${list.length ? Math.round((shallow / list.length) * 100) : 0}%</dd>
      <dt>Felt reports</dt><dd>${felt ? num(felt) : '—'}</dd>
    </dl>
    <div class="panel-sub" style="margin:0">Largest events here — click one for detail</div>
    <div class="rank-list">${rows}</div>`;
  const el = openDrawerWithDelegation(name, body);
  el?.querySelectorAll('[data-event]').forEach((r) => {
    r.addEventListener('click', () => {
      const q = list.find((x) => x.id === r.getAttribute('data-event'));
      if (q) openQuakeDrawer(q);
    });
  });
}

function openDrawerWithDelegation(title: string, body: string): HTMLElement | null {
  openDrawer(title, body);
  return document.querySelector('.drawer');
}

export { closeDrawer, gterm };
